import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Stack,
  Divider,
  Badge,
  CircularProgress,
  Modal,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  ListItemAvatar,
  Autocomplete,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
} from "@mui/material";
import {
  Search,
  Refresh,
  Today,
  DateRange,
  ArrowForward,
  ArrowBack,
  Close,
  AccountBalance,
  CreditCard,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  approvedOrders,
  getAllOrders,
  getAllOrders_club,
  getMyOrders,
  orderReport,
  processPayment,
  sendOTP,
} from "../../../api/orders/orders";
import {
  IonContent,
  IonHeader,
  IonPage,
  IonTabBar,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import { formatToNaira } from "../../../common/utils_t";
import { useQuery } from "react-query";
import { getAllMembers } from "../../../api/users/user";
import POSDialog from "../../../components/pos/PosDialog";
import { getBarInventory, getBarInventoryByBranch, getCategories } from "../../../api/products/product";
import { generateToken, verifyToken } from "../../../common/utils";

// Types
interface OrderItem {
  name: string;
  quantity: number;
  price: number;
}

interface Order {
  id: string;
  createdAt: string;
  order_status: "preparing" | "ready" | "COMPLETED" | "cancelled" | "WAITING";
  items: OrderItem[];
  total: number;
  notes?: string;
  member_id?: string;
  payment_status?: number;
}

interface ApiResponse {
  data: Order[];
  total: number;
}

const statusColors = {
  preparing: "warning",
  ready: "success",
  served: "success",
  completed: "success",
  cancelled: "error",
};

const BarOrdersPage = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [memberId, setMemberId] = useState("");
  const [otp, setOtp] = useState("");
  const [status, setStatus] = useState({ isLoading: false, error: false });

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);

  // const [memberId, setMemberId] = useState('');

  const [selectedMember, setSelectedMember] = useState(null);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState<
    "card" | "transfer" | "wallet" | null
  >(null);
  const [posDialogOpen, setPosDialogOpen] = useState(false);
  const [selectedPaymentOption, setSelectedPaymentOption] = useState("");
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  const [selectedEditOrder, setSelectedEditOrder] = useState<Order | null>(
    null
  );
  const [errors, setErrors] = useState("");

  const [dateRange, setDateRange] = useState<{
    start: Date | null;
    end: Date | null;
  }>({ start: null, end: null });
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
  });
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  const {
    data: members = [],
    isLoading,
    error: isError,
  } = useQuery("getMemberList", getAllMembers, {
    refetchOnWindowFocus: true,
    retry: true,
    onError: (error) => {
      console.error("Error fetching inventory data:", error);
    },
  });

  // Format date to YYYY-MM-DD for API
  const formatDateForApi = (date: Date | null) => {
    if (!date) return "";
    return date.toISOString().split("T")[0];
  };

  // Fetch orders from API
  const fetchOrders = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        page: pagination.page,
        pageSize: pagination.pageSize,
        search: searchTerm,
        start_date: formatDateForApi(dateRange.start),
        end_date: formatDateForApi(dateRange.end),
      };

      const response = await getAllOrders_club(params);
      console.log({ response });
      setOrders(response.data);

      setPagination((prev) => ({
        ...prev,
        total: response.data?.length,
      }));
    } catch (err) {
      setError("Failed to fetch orders. Please try again.");
      console.error("Error fetching orders:", err);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch and when filters change
  // useEffect(() => {
  //   fetchOrders();
  // }, [dateRange]);

  useEffect(() => {
    // Call once immediately
    fetchOrders();

    // Set interval to call every 10 seconds
    /*
    const interval = setInterval(() => {
      fetchOrders();
    }, 10000); // 10000ms = 10s
 */
    // Clear the interval on component unmount
    //return () => clearInterval(interval);
  }, [dateRange]);

  // Time formatting
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  // Date formatting
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  // Date range display text
  const getDateRangeText = () => {
    if (!dateRange.start && !dateRange.end) return "All dates";
    if (dateRange.start && !dateRange.end)
      return `From ${formatDate(dateRange.start.toISOString())}`;
    if (!dateRange.start && dateRange.end)
      return `Until ${formatDate(dateRange.end.toISOString())}`;
    return `${formatDate(dateRange.start?.toISOString() || "")} - ${formatDate(
      dateRange.end?.toISOString() || ""
    )}`;
  };

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({
      ...prev,
      page: Math.max(
        1,
        Math.min(newPage, Math.ceil(pagination.total / pagination.pageSize))
      ),
    }));
  };

  // Open order details modal
  const handleOpenOrderDetails = (order: Order) => {
    setSelectedOrder(order);
  };

  // Close order details modal
  const handlePayment = () => {
    setSelectedOrder(null);
  };
  const handleCloseOrderDetails = () => {
    setSelectedOrder(null);
  };

  const handleProcessOrder = async (status: "COMPLETED" | "cancelled") => {
    try {
      const payload = {
        order_id: selectedOrder.order_id,
        status: status,
      };
      await approvedOrders(payload);
      setSelectedOrder(null);
      fetchOrders();
    } catch (error) {
      console.error("Error processing order:", error);
      // Handle error (show toast, etc.)
    }
  };

  const handleProcessPayment = async () => {
    try {
      const payload = {
        order_id: selectedOrder?.order_id,
        paymentMethod: paymentMethod,
        memberId: selectedOrder?.member_id,
        selectedPaymentOption: selectedPaymentOption,
        otp: otp,
      };

      const response = await processPayment(payload);
      console.log("Payment response:", response);
      alert(response.message);
      setOtp("");
      setPaymentMethod(null);
      setSelectedPaymentOption("");
      setLockPay(true);
      setResult("");

      // Close dialogs and refresh
      setShowPaymentDialog(false);
      handleCloseOrderDetails();
      fetchOrders();

      // Show success message
      setToastMessage("Payment processed successfully!");
      setShowToast(true);
    } catch (error) {
      console.error("Payment processing error:", error);
      setToastMessage("Payment failed. Please try again.");
      setShowToast(true);
    }
  };

  // console.log({ members });

  useEffect(() => {
    if (memberId.trim() !== "") {
      const filtered = members.data?.filter((member) =>
        member.uname.toLowerCase().includes(memberId)
      );
      setFilteredMembers(filtered);
    } else {
      setFilteredMembers([]);
    }
  }, [memberId, members]);

  const handleClosePosDialog = () => {
    setPosDialogOpen(false);
  };

  const handleEditOrder = async (order) => {
    setSelectedEditOrder(order);
    setPosDialogOpen(true);
  };

  const handleUpdateOrder = (updatedOrder) => {
    setOrders(
      orders.map((order) =>
        order.id === updatedOrder.id ? updatedOrder : order
      )
    );
    setPosDialogOpen(false);
    setViewDialogOpen(false);
  };

  const fetchProducts = async () => {
    try {
      const [response1, response2] = await Promise.all([
        getBarInventoryByBranch(),
        getCategories(),
      ]);

      // console.log([response1, response2])

      setProducts(response1.data);
      setCategories(response2.data);
    } catch (error) {
      console.error("Error fetching products and categories:", error);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleOTP = async () => {
    const payload = {
      order_id: selectedOrder?.order_id,
      paymentMethod: selectedPaymentOption,
      memberId: selectedOrder.member_id,
      selectedOrder: selectedOrder,
    };
    console.log({ payload });
    // console.log({payload})

    // setStatus({ isLoading: true, error: false });
    try {
      const response = await sendOTP(payload);
      console.log("otp", response);

      setStatus({ isLoading: false, error: false });
    } catch (error: any) {
      setStatus({ isLoading: false, error: true });
      if (error.response) {
        setErrors(error.response.data.error || error.response.data.message);
      } else {
        console.error("An unexpected error occurred:", error);
      }
    }
  };

  //const [memberId, setMemberId] = useState('1024');
  const [lockPay, setLockPay] = useState(true);
  const [token, setToken] = useState("");
  const [result, setResult] = useState("");
  const expirationType = "1min";

  const handleVerify = async (newToken) => {
    const res = await verifyToken(
      newToken,
      selectedOrder.member_id,
      expirationType
    );

    res.valid ? setLockPay(false) : setLockPay(true);

    setResult(res.valid ? `✅ Correct App-OTP` : "❌ Invalid or expired OTP");
    console.log(
      res.valid
        ? `✅ Correct App-OTP ${selectedOrder.member_id}`
        : `❌ Invalid or expired OTP ${selectedOrder.member_id}`
    );
  };

  useEffect(() => {
    if (selectedPaymentOption === "tab") {
      setLockPay(false);
    }
  }, [selectedPaymentOption]);
  return (
    <IonPage className="pushDown" >
      <IonHeader>
        <IonHeader>
          <IonToolbar>
            <IonTitle>Bar Orders</IonTitle>
          </IonToolbar>
        </IonHeader>
      </IonHeader>

      <IonContent>
        <Box sx={{ p: 3 }}>
          {/* Filters Section */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <TextField
                placeholder="Search orders or items..."
                variant="outlined"
                size="small"
                fullWidth
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && fetchOrders()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />

              <DatePicker
                label="Start Date"
                value={dateRange.start}
                onChange={(newValue) =>
                  setDateRange((prev) => ({ ...prev, start: newValue }))
                }
                renderInput={(params) => <TextField {...params} size="small" />}
                components={{
                  OpenPickerIcon: DateRange,
                }}
              />

              <DatePicker
                label="End Date"
                value={dateRange.end}
                onChange={(newValue) =>
                  setDateRange((prev) => ({ ...prev, end: newValue }))
                }
                renderInput={(params) => <TextField {...params} size="small" />}
                components={{
                  OpenPickerIcon: DateRange,
                }}
                minDate={dateRange.start}
              />

              <Button
                variant="outlined"
                onClick={() => setDateRange({ start: null, end: null })}
              >
                Clear Dates
              </Button>

              <IconButton onClick={fetchOrders} disabled={loading}>
                <Refresh />
              </IconButton>
            </Stack>

            <Typography variant="body2" color="text.secondary">
              {getDateRangeText()} • Showing {orders?.length} of{" "}
              {pagination.total} orders
            </Typography>
          </Paper>

          {/* Loading and Error States */}
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Paper sx={{ p: 2, mb: 3, bgcolor: "error.light" }}>
              <Typography color="error">{error}</Typography>
              <Button onClick={fetchOrders} sx={{ mt: 1 }}>
                Retry
              </Button>
            </Paper>
          )}

          {/* Orders Table */}
          {!loading && !error && (
            <>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>S/N</TableCell>
                      <TableCell>Date/Time</TableCell>
                      <TableCell>Order By:</TableCell>

                      <TableCell>Items Count</TableCell>

                      <TableCell align="right">Total</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orders?.length > 0 ? (
                      orders?.map((order, i) => (
                        <TableRow key={i} hover>
                          <TableCell>
                            <Typography fontWeight="bold">{i + 1}</Typography>
                          </TableCell>
                          <TableCell>
                            {formatDate(order.order_date)} •{" "}
                            {formatTime(order.order_date)}
                          </TableCell>
                          <TableCell>{order.order_by} </TableCell>

                          <TableCell>{order.items.length} items</TableCell>
                          <TableCell align="right">
                            {formatToNaira(order.payable_amount)}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={order.order_status}
                              color={
                                statusColors[
                                  order?.order_status.toLowerCase()
                                ] || "default"
                              }
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="outlined"
                              endIcon={<ArrowForward />}
                              onClick={() => handleOpenOrderDetails(order)}
                            >
                              Details
                            </Button>
                          </TableCell>
                          {/* <TableCell align="right">
                            <Button
                              size="small"
                              variant="outlined"
                              endIcon={<ArrowForward />}
                              onClick={() => handleEditOrder(order)}
                            >
                              Edit
                            </Button>
                          </TableCell> */}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                          <Typography variant="body1" color="text.secondary">
                            No orders found matching your criteria
                          </Typography>
                          <Button
                            variant="text"
                            startIcon={<Refresh />}
                            onClick={fetchOrders}
                            sx={{ mt: 1 }}
                          >
                            Refresh
                          </Button>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}
              >
                <Button
                  startIcon={<ArrowBack />}
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1 || loading}
                >
                  Previous
                </Button>

                <Typography variant="body1">
                  Page {pagination.page} of{" "}
                  {Math.ceil(pagination.total / pagination.pageSize)}
                </Typography>

                <Button
                  endIcon={<ArrowForward />}
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={
                    pagination.page * pagination.pageSize >= pagination.total ||
                    loading
                  }
                >
                  Next
                </Button>
              </Box>
            </>
          )}

          {/* Order Details Modal */}
          <Dialog
            open={Boolean(selectedOrder)}
            onClose={handleCloseOrderDetails}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="h6">
                  Order #{selectedOrder?.order_code}
                </Typography>
                <IconButton onClick={handleCloseOrderDetails}>
                  <Close />
                </IconButton>
              </Box>
              <Typography variant="subtitle2" color="text.secondary">
                {selectedOrder &&
                  `${formatDate(selectedOrder.order_date)} • ${formatTime(
                    selectedOrder.order_date
                  )}`}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                Order By: {selectedOrder?.order_by}
              </Typography>
            </DialogTitle>

            <DialogContent dividers>
              {selectedOrder && (
                <>
                  <Box mb={2}>
                    <Chip
                      label={selectedOrder.order_status}
                      color={
                        statusColors[selectedOrder.order_status] || "default"
                      }
                      size="medium"
                    />
                  </Box>

                  <List dense>
                    {selectedOrder.items.map((item, index) => (
                      <ListItem key={index}>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: "primary.main" }}>
                            {item?.productName.charAt(0)}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={item?.productName}
                          secondary={
                            <>
                              <Box component="span" display="block">
                                Qty: {item.quantity}
                              </Box>
                              <Box component="span" display="block">
                                Price: {formatToNaira(item.price)}
                              </Box>
                              {item.item_status && (
                                <Chip
                                  label={item.item_status}
                                  size="small"
                                  color={
                                    item.item_status === "approved"
                                      ? "success"
                                      : item.item_status === "rejected"
                                      ? "error"
                                      : "warning"
                                  }
                                  sx={{ mt: 0.5 }}
                                />
                              )}
                            </>
                          }
                        />
                        <ListItemSecondaryAction>
                          <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="flex-end"
                          >
                            <Typography fontWeight="bold">
                              {formatToNaira(item.price * item.quantity)}
                            </Typography>
                          </Box>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>

                  <Divider sx={{ my: 2 }} />

                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="subtitle1">Subtotal:</Typography>
                    <Typography variant="subtitle1">
                      {formatToNaira(selectedOrder.payable_amount)}
                    </Typography>
                  </Box>

                  {selectedOrder.notes && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2">Notes:</Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {selectedOrder.notes}
                      </Typography>
                    </>
                  )}

                  <Divider sx={{ my: 2 }} />

                  {selectedOrder.order_status == "WAITING" && (
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="subtitle1">Order Status:</Typography>
                      <Box display="flex" gap={1}>
                        <Button
                          variant="contained"
                          color="success"
                          onClick={() => handleProcessOrder("COMPLETED")}
                        >
                          Complete Order
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={() => handleProcessOrder("cancelled")}
                        >
                          Cancel Order
                        </Button>
                      </Box>
                    </Box>
                  )}
                  {selectedOrder.order_status === "COMPLETED" &&
                    selectedOrder.payment_status == 0 && (
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Box display="flex" gap={1}>
                          <Button
                            variant="contained"
                            color="success"
                            onClick={() => setShowPaymentDialog(true)}
                          >
                            Proceed to payment
                          </Button>
                        </Box>
                      </Box>
                    )}
                </>
              )}
            </DialogContent>

            <DialogActions>
              <Button
                onClick={handleCloseOrderDetails}
                variant="contained"
                color="primary"
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {/* Payment Method Selection Dialog */}
          <Dialog
            open={showPaymentDialog}
            onClose={() => setShowPaymentDialog(false)}
            fullWidth
            maxWidth="xs"
          >
            <DialogTitle>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="h6">Select Payment Method</Typography>
                <IconButton onClick={() => setShowPaymentDialog(false)}>
                  <Close />
                </IconButton>
              </Box>
            </DialogTitle>

            <DialogContent dividers>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Button
                  variant={paymentMethod === "card" ? "contained" : "outlined"}
                  color="primary"
                  size="large"
                  fullWidth
                  onClick={() => {
                    setPaymentMethod("card"), handleProcessPayment();
                  }}
                  startIcon={<CreditCard />}
                  sx={{ py: 2 }}
                >
                  Card Payment
                </Button>
                <Button
                  variant={
                    paymentMethod === "wallet" ? "contained" : "outlined"
                  }
                  color="primary"
                  size="large"
                  fullWidth
                  onClick={() => setPaymentMethod("wallet")}
                  startIcon={<CreditCard />}
                  sx={{ py: 2 }}
                >
                  Wallet Payment
                </Button>

                <Button
                  variant={paymentMethod === "card" ? "contained" : "outlined"}
                  color="primary"
                  size="large"
                  fullWidth
                  onClick={() => {
                    setPaymentMethod("transfer"), handleProcessPayment();
                  }}
                  startIcon={<CreditCard />}
                  sx={{ py: 2 }}
                >
                  Bank Transfer
                </Button>
                {/*     <Button
                  variant={
                    paymentMethod === "transfer" ? "contained" : "outlined"
                  }
                  color="secondary"
                  size="large"
                  fullWidth
                  onClick={() => setPaymentMethod("transfer")}
                  startIcon={<AccountBalance />}
                  sx={{ py: 2 }}
                >
                  Bank Transfer
                </Button> */}

                {/*  {paymentMethod === "transfer" && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      Please transfer to:
                    </Typography>
                    <Typography variant="body2">
                      Bank Name: <strong>MoniPoint Bank</strong>
                    </Typography>
                    <Typography variant="body2">
                      Account Number: <strong>1234567890</strong>
                    </Typography>
                    <Typography variant="body2">
                      Account Name: <strong>Your Business Name</strong>
                    </Typography>
                  </Box>
                )} */}

                {paymentMethod === "wallet" && (
                  <Box sx={{ mt: 2 }}>
                    <FormControl fullWidth>
                      <InputLabel>Select Payment Option</InputLabel>
                      <Select
                        label="Select Payment Option"
                        value={selectedPaymentOption} // you should define this in state
                        onChange={(e) =>
                          setSelectedPaymentOption(e.target.value)
                        }
                      >
                        <MenuItem value="">
                          <em>Select Payment Option</em>
                        </MenuItem>
                        <MenuItem value="wallet_qrcode">
                          Pay with Wallet(MemberApp-Code)
                        </MenuItem>
                        {/*                         <MenuItem value="sms">Pay with Wallet(SMS)</MenuItem>
                         */}{" "}
                        <MenuItem value="tab">Pay with Tab</MenuItem>
                      </Select>
                    </FormControl>
                    {selectedPaymentOption == "wallet_qrcode" && (
                      <Box>
                        {/* {selectedOrder?.otp_status == "0" && ( */}

                        {/* // )} */}

                        <TextField
                          label="App Token"
                          variant="outlined"
                          value={otp}
                          fullWidth
                          margin="normal"
                          onChange={(e) => {
                            const input = e.target.value;
                            // Remove the CAAS- part if it's manually typed again
                            //const newValue = input.replace(/^CAAS-/, "");
                            setOtp(input);
                            handleVerify(input);
                          }}
                          disabled={!selectedOrder?.member_id}
                        />
                        <Typography
                          variant="body2"
                          color={
                            result.includes("Correct")
                              ? "success.main"
                              : "error.main"
                          }
                        >
                          {result}
                        </Typography>
                      </Box>
                    )}
                    {selectedPaymentOption == "sms" && (
                      <Box>
                        {/* {selectedOrder?.otp_status == "0" && ( */}
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => handleOTP()}
                          // disabled={}
                        >
                          Send OTP
                        </Button>
                        {/* // )} */}

                        <TextField
                          label="OTP"
                          variant="outlined"
                          value={`CAAS-${otp}`}
                          fullWidth
                          margin="normal"
                          onChange={(e) => {
                            const input = e.target.value;
                            // Remove the CAAS- part if it's manually typed again
                            const newValue = input.replace(/^CAAS-/, "");
                            setOtp(newValue);
                          }}
                          disabled={!selectedOrder?.member_id}
                        />
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </DialogContent>

            <DialogActions>
              <Button
                onClick={() => setShowPaymentDialog(false)}
                color="inherit"
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleProcessPayment}
                disabled={
                  !paymentMethod ||
                  (paymentMethod == "wallet" && !selectedPaymentOption) ||
                  lockPay
                }
              >
                Confirm Payment
              </Button>
            </DialogActions>
          </Dialog>

          {selectedEditOrder && (
            <POSDialog
              open={posDialogOpen}
              onClose={handleClosePosDialog}
              order={selectedEditOrder}
              onSave={handleUpdateOrder}
              categories={categories}
              products={products}
            />
          )}
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default BarOrdersPage;
